import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

// Types
export interface PartnerAnalytics {
  users: {
    total: number;
    active: number;
  };
  usage: {
    totalCalls: number;
    totalMinutes: number;
  };
  revenue: {
    monthStart: string;
    subscriptions: number;
    boostPacks: number;
    total: number;
    commissionRate: number;
    commissionAmount: number;
  };
}

// API functions
const fetchPartnerAnalytics = async (): Promise<PartnerAnalytics> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-analytics?action=overview`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch partner analytics');
  }

  return response.json();
};

// Hooks
export const usePartnerAnalytics = () => {
  return useQuery({
    queryKey: ['partner', 'analytics'],
    queryFn: fetchPartnerAnalytics,
    staleTime: 2 * 60 * 1000, // 2 minutes - analytics need to be relatively fresh
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes in background
  });
};

// Helper hook for specific metrics
export const usePartnerMetrics = () => {
  const { data: analytics, ...rest } = usePartnerAnalytics();
  
  return {
    ...rest,
    data: analytics ? {
      totalUsers: analytics.users.total,
      activeUsers: analytics.users.active,
      totalCalls: analytics.usage.totalCalls,
      totalMinutes: analytics.usage.totalMinutes,
      totalRevenue: analytics.revenue.total,
      commissionAmount: analytics.revenue.commissionAmount,
      commissionRate: analytics.revenue.commissionRate,
      avgCallsPerUser: analytics.users.total > 0 ? Math.round(analytics.usage.totalCalls / analytics.users.total) : 0,
      avgMinutesPerCall: analytics.usage.totalCalls > 0 ? Math.round(analytics.usage.totalMinutes / analytics.usage.totalCalls) : 0,
    } : undefined
  };
};